import React, { useCallback, useMemo, useState } from 'react';
import { styled } from 'styled-components';
import { Divider, Statistic } from 'antd';
import { IconButton, IconButton_IconSizes, OverlayMessage, RowCentered } from '@ydistri/ds';
import { CalculationMode, ConfigurationStatus } from '@ydistri/api-sdk';
import { COLORS } from '../../../styles/style';
import { useTemplateStatus } from '../../../hooks/useTemplateStatus';
import { Overlay } from '../Overlay';
import { usePatchTemplateStatusMutation } from '../../../screens/Configuration/apiTemplate';
import { AvailableModals, openModal } from '../../../screens/Modals/modalSlice';
import { MdDirectionsRun, MdOpenInNew, MdReplay } from 'react-icons/md';
import PotentialValues from './PotentialValues';
import Validator from '../../../screens/Configuration/Validator/Validator';
import { useAppDispatch } from '../../../store';
import CreateCalculationButton, {
  CreateCalculationButtonProps,
} from '../../../screens/Configuration/CreateCalculationButton';
import { useSelectedTemplateId } from '../../../hooks/useSelectedTemplateId';
import { TbRefreshAlert } from 'react-icons/tb';
import { useTemplateOrCalculationWithValue } from '../../../hooks/useTemplateOrCalculationWithValue';

const PotentialOverviewWrapper = styled(RowCentered)<{ $isOutdated: boolean }>`
  flex-wrap: nowrap;
  font-size: 1.5rem;
  color: ${COLORS.ALL_SKU_TYPES_COLOR};
  background-color: ${p => (p.$isOutdated ? COLORS.GREY_LIGHT : COLORS.PRIMARY_STEPS[0])};
  border-radius: 0.5rem;
  gap: 0.5rem;
  padding: 0.25rem 1rem;

  .ant-statistic-title {
    font-size: 0.8rem;
    margin-bottom: 0;
  }

  .ant-statistic-content {
    font-size: 1rem;
  }
`;

const PotentialStatistic = styled(Statistic)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 7.5rem;

  .ant-statistic-content-value {
    font-weight: 500;
  }
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: 0.3s all;
  //cursor: pointer;
  gap: 0.5rem;
`;

const createCalculationInitialValues: CreateCalculationButtonProps['initialValues'] = {
  title: '',
  description: '',
  private: false,
  calculationMode: CalculationMode.Computed,
};

const TemplatePotentialOverview: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const selectedTemplateId = useSelectedTemplateId();
  const dispatch = useAppDispatch();
  const { isRunning, isOutdated, niceName } = useTemplateStatus();
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [recomputeTemplateMutation, recomputeTemplateMutationStatus] =
    usePatchTemplateStatusMutation();

  const onValidChange = useCallback((valid: boolean) => setButtonDisabled(!valid), []);
  const openModalIcon = useMemo(() => <MdOpenInNew size={IconButton_IconSizes.small} />, []);
  const recomputeIcon = useMemo(() => <MdReplay size={IconButton_IconSizes.small} />, []);
  const potentialValuesOverlayMessage = useMemo(
    () => (
      <OverlayMessage>
        <TbRefreshAlert /> Outdated results
      </OverlayMessage>
    ),
    [],
  );
  const runningMessage = useMemo(
    () => (
      <OverlayMessage>
        <MdDirectionsRun /> {niceName}
      </OverlayMessage>
    ),
    [niceName],
  );

  const recomputeTemplate = useCallback(() => {
    if (selectedTemplateId) {
      recomputeTemplateMutation({
        templateId: selectedTemplateId,
        data: { status: ConfigurationStatus.RefreshPotential },
      });
    }
  }, [recomputeTemplateMutation, selectedTemplateId]);

  const openRootCategoryDetail = useCallback(() => {
    dispatch(openModal({ type: AvailableModals.CATEGORY_INSIGHTS, data: undefined }));
  }, [dispatch]);

  return (
    <PotentialOverviewWrapper $isOutdated={isOutdated}>
      <Overlay active={isRunning} overlayContent={runningMessage}>
        <PotentialStatistic title="Status" value={niceName} />
        <Divider type="vertical" />
        <Overlay active={!isRunning && isOutdated} overlayContent={potentialValuesOverlayMessage}>
          <PotentialValues calculationMode={CalculationMode.Computed} />
        </Overlay>
        <Divider type="vertical" />
        <IconsWrapper>
          <IconButton
            size="small"
            icon={openModalIcon}
            tooltip="Open detailed statistics"
            tooltipPosition="left"
            onClick={openRootCategoryDetail}
          />
          <IconButton
            size="small"
            icon={recomputeIcon}
            loading={recomputeTemplateMutationStatus.isLoading}
            tooltip="Recompute statistics"
            tooltipPosition="left"
            disabled={!isOutdated}
            onClick={recomputeTemplate}
          />
          <Validator
            onValidChange={onValidChange}
            templateOrCalculation={templateOrCalculation}
            size="large"
          />
          <CreateCalculationButton
            disabled={buttonDisabled}
            initialValues={createCalculationInitialValues}
          />
        </IconsWrapper>
      </Overlay>
    </PotentialOverviewWrapper>
  );
};

export default TemplatePotentialOverview;
